import React, { useState, useEffect } from "react";
import { Table, message, Alert, Modal, Form, Input, Select, Spin, Grid } from "antd";
import ProcessDeviceMultipleFiles from "./ProcessDeviceMultipleFiles";
import { getAPI, deleteAPI, putAPI } from "../../../config/api/apiConfig";
import { DeleteOutlined, EditOutlined, LoadingOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { setListDevice } from "../../../redux/feature/device";

const { Option } = Select;
const { useBreakpoint } = Grid;

const ListDevice2 = () => {
  const [deviceData, setDeviceData] = useState([]);
  const [editingDevice, setEditingDevice] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalDevices, setTotalDevices] = useState(0);

  const screens = useBreakpoint();
  const isSmallScreen = screens.xs || screens.sm;

  const dispatch = useDispatch();
  const loggedInUserId = useSelector((state) => state.authenticate.userId);
  const token = useSelector((state) => state.authenticate.userToken);
  const [form] = Form.useForm();

  const deleteDevice = async (userId, deviceId) => {
    try {
      await deleteAPI(`/delete-device/${userId}/${deviceId}`);
      setDeviceData(prevData => prevData.filter(device => device.id !== deviceId));
      message.success("Device deleted successfully");
    } catch (err) {
      message.error("Device not deleted");
    }
  };

  const updateDevice = async (userId, deviceId, values) => {
    setLoading(true);
    const deviceToUpdate = {
      userId: parseInt(values.userId, 10),
      deviceName: values.deviceName,
      deviceType: values.deviceType,
      deviceModel: values.deviceModel,
      organisationName: values.organisationName,
    };
    try {
      await putAPI(`/update-device/${parseInt(userId)}/${parseInt(deviceId)}`, deviceToUpdate);
      setDeviceData(prevData => prevData.map(dev => dev.id === deviceId ? { ...dev, ...deviceToUpdate } : dev));
      setEditingDevice(null); // Clear editing device state
      message.success("Device updated successfully");
      setLoading(false);
      setModalOpen(false);
    } catch (err) {
      setLoading(false);
      message.error(err?.response?.data?.message);
      setModalOpen(false);
    }
  };

  const getDevices = async () => {
    try {
      const devicesResponse = await getAPI(`/get-all-devices?page=${currentPage}&limit=${pageSize}`, token);
      const { data, pagination } = devicesResponse.data;
      setDeviceData(data);
      setTotalDevices(pagination.totalDevices);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getDevices();
  }, [currentPage, pageSize]);

  const handleExpand = (expanded, record) => {
    setExpandedRowKeys(expanded ? [record.key] : []);
  };

  const handleEdit = (deviceId) => {
    const device = deviceData.find(dev => dev.id === deviceId);
    setEditingDevice(device);
    form.setFieldsValue(device); // Reset form with the latest data
    setModalOpen(true);
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      updateDevice(loggedInUserId, editingDevice.id, values);
      form.resetFields();
    });
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const columns = [
    { title: "Device Type", dataIndex: "deviceType", key: "deviceType" },
    { title: "Device Name", dataIndex: "deviceName", key: "deviceName" },
    { title: "Device Model", dataIndex: "deviceModel", key: "deviceModel" },
    { title: "Organisation Name", dataIndex: "organisationName", key: "organisationName" },
    { title: "Status", dataIndex: "status", key: "status" },
    {
      title: "Edit", dataIndex: "", key: "x",
      render: (item) => (
        <>
          <a onClick={() => handleEdit(item.key)}><EditOutlined /></a>
        </>
      )
    },
    {
      title: "Delete", dataIndex: "", key: "x",
      render: (item) => (
        <>
          <a onClick={() => deleteDevice(loggedInUserId, item.key)}><DeleteOutlined /></a>
        </>
      )
    }
  ];

  const dataSource = deviceData.map(device => ({
    key: device.id,
    ...device,
    description: (
      <ProcessDeviceMultipleFiles
        refreshDevices={getDevices}
        listedDeviceId={device.id}
        status={device.status}
        organisationName={device.organisationName}
        deviceName={device.deviceName}
      />
    ),
  }));

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  return (
    deviceData.length ? (
      <>
        <Table
          columns={columns}
          scroll={{ x: isSmallScreen ? 'max-content' : undefined }}
          expandable={{
            expandedRowRender: record => <p style={{ margin: 0 }}>{record.description}</p>,
            rowExpandable: record => record.deviceName !== "Not Expandable",
            expandedRowKeys,
            onExpand: handleExpand,
          }}
          dataSource={dataSource}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalDevices,
            showTotal: total => `Total ${total} items`,
            onChange: (page, pageSize) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            },
          }}
        />

        <Modal
          title={`Editing ${editingDevice?.deviceName} ...`}
          centered
          visible={modalOpen}
          onOk={handleModalOk}
          okButtonProps={{ loading, disabled: loading, style: { width: "40%" }, className: "nibss-form-btn" }}
          onCancel={handleModalCancel}
        >
          <Form form={form} layout="vertical" className="row-col" initialValues={editingDevice}>
            <Form.Item label="Device Name" name="deviceName" rules={[{ required: true, message: "Please input your device name!" }]}>
              <Input placeholder="Enter your Device Name" />
            </Form.Item>
            <Form.Item label="Device Type" name="deviceType" rules={[{ required: true, message: "Please input your device type!" }]}>
              <Select placeholder="Select a Device Type">
                <Option value="single">Single</Option>
                <Option value="multiple">Multiple</Option>
              </Select>
            </Form.Item>
            <Form.Item label="Device Model" name="deviceModel" rules={[{ required: true, message: "Please input Device Model" }]}>
              <Input placeholder="Device Model" />
            </Form.Item>
            <Form.Item label="Organisation Name" name="organisationName" rules={[{ required: true, message: "Please input Organisation Name" }]}>
              <Input placeholder="Organisation Name" />
            </Form.Item>
            <Form.Item name="userId" style={{ display: "none" }} initialValue={loggedInUserId}>
              <Input />
            </Form.Item>
          </Form>
        </Modal>
      </>
    ) : (
      deviceData === undefined ? (
        <Spin indicator={antIcon} style={{ width: "100%", margin: "30% 0", padding: "30px 50px", textAlign: "center", borderRadius: "4px" }} />
      ) : (
        <Alert message="No Device" description="No pending device for review and approval" className="alert" showIcon />
      )
    )
  );
};

export default ListDevice2;
